import React from "react";
import { SectionTitle } from "../common";
import PhotoGallery from "../sections/PhotoGallery";
import { useAbout } from "../../hooks/useAbout";
import { Photo } from "../../types";

const getYoutubeEmbedUrl = (watchUrl?: string) => {
  if (!watchUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(watchUrl);

    if (parsedUrl.hostname.includes("youtu.be")) {
      const id = parsedUrl.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }

    if (parsedUrl.hostname.includes("youtube.com")) {
      const id = parsedUrl.searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch (error) {
    console.error("Invalid YouTube URL provided for about video", error);
  }

  return null;
};

const Introduction: React.FC = () => {
  const { about } = useAbout();
  const descriptionParagraphs = about?.description
    ? about.description
        .split("\n")
        .map((text) => text.trim())
        .filter((text) => text.length > 0)
    : [];
  const videoSrc = getYoutubeEmbedUrl(about?.url_link || "");

  // Dynamically collect all image fields from the about object
  const introductionPhotos: Photo[] = about
    ? Object.keys(about)
        .filter((key) => key.startsWith("image") && about[key])
        .sort((a, b) => {
          // Sort by image number (image1, image2, image3, etc.)
          const numA = parseInt(a.replace("image", "")) || 0;
          const numB = parseInt(b.replace("image", "")) || 0;
          return numA - numB;
        })
        .map((key, index) => ({
          src: about[key] as string,
          alt: `About Introduction Photo ${index + 1}`,
          title: "About Introduction",
        }))
    : [];

  return (
    <section id="section-about">
      <div id="animated-bg-container" style={{ position: "relative" }}>
        <div className="container">
          <SectionTitle
            subtitle="[ Introduction ]"
            title="Welcome to the AI Literacy Hub"
          />

          <div className="row">
            {about ? (
              <>
                <div className="col-lg-6">
                  {descriptionParagraphs.map((text, index) => {
                    if (index === 0) {
                      return (
                        <p key={text.substring(0, 20) + index} className="lead">
                          {text}
                        </p>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="col-lg-6">
                  {videoSrc && (
                    <div className="video-wrapper mb-4">
                      <div className="ratio ratio-16x9">
                        <iframe
                          src={videoSrc}
                          title="About Introduction Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="col-12">
                <div className="text-center">
                  <p>Loading...</p>
                </div>
              </div>
            )}
          </div>

          {about && descriptionParagraphs.length > 1 && (
            <div className="row mt-4">
              <div className="col-12">
                {descriptionParagraphs.map((text, index) => {
                  if (index > 0) {
                    return (
                      <p key={text.substring(0, 20) + index}>
                        {text}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}

          {introductionPhotos.length > 0 && (
            <div className="row mt-5">
              <div className="col-12">
                <PhotoGallery photos={introductionPhotos} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Introduction;
